/**
 * Утилиты применения цветов профиля.
 * Контракт: цвета приходят строкой "#RRGGBB" или null; null — цвет из стандартной
 * темы приложения (не чёрный и не прозрачный).
 */

// Стандартная тема приложения (docs/design-system.md)
export const PROFILE_THEME = {
  bg: '#0A0A0F',
  surface: '#14141A',
  text: '#EAEAEA',
  textSecondary: '#A0A0A0',
  accent: '#00FF7F'
}

// Восемь цветовых ключей (bg_angle — отдельное числовое поле)
export const COLOR_KEYS = ['nick', 'bg', 'bg2', 'accent', 'avatar_frame', 'card_bg', 'content_bg', 'text_secondary']

export const isValidHex = (value) => typeof value === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value)

export const normalizeHex = (value) => (isValidHex(value) ? value.toUpperCase() : null)

const channel = (c) => {
  const v = c / 255
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
}

export const luminance = (hex) => {
  const n = parseInt(hex.slice(1), 16)
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255)
}

export const contrastRatio = (hexA, hexB) => {
  const l1 = luminance(hexA)
  const l2 = luminance(hexB)
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05)
}

/**
 * Фон профиля: цвет или градиент.
 * bg2 без bg — градиент от цвета темы к bg2. Угол по умолчанию 180.
 */
export const profileBackground = (colors) => {
  const bg = isValidHex(colors?.bg) ? colors.bg : PROFILE_THEME.bg
  if (isValidHex(colors?.bg2)) {
    const angle = Number.isFinite(colors?.bg_angle) ? Math.round(colors.bg_angle) : 180
    return `linear-gradient(${angle}deg, ${bg}, ${colors.bg2})`
  }
  return bg
}

/** Основной текст: светлый фон → тёмный текст и наоборот */
export const primaryTextColor = (colors) => {
  const bg = isValidHex(colors?.bg) ? colors.bg : PROFILE_THEME.bg
  return luminance(bg) > 0.5 ? PROFILE_THEME.surface : PROFILE_THEME.text
}

/** Второстепенный текст (подписи, «N мин назад») — вычисляется из фона */
export const secondaryTextColor = (colors) => {
  const bg = isValidHex(colors?.bg) ? colors.bg : PROFILE_THEME.bg
  return luminance(bg) > 0.5 ? '#3A3A44' : PROFILE_THEME.textSecondary
}

export const accentColor = (colors) => (isValidHex(colors?.accent) ? colors.accent : PROFILE_THEME.accent)

/** Осветлить hex в сторону белого (ratio 0–1) — вторая точка градиентов под кастомный акцент */
export const lightenHex = (hex, ratio = 0.35) => {
  const n = parseInt(hex.slice(1), 16)
  const mix = (c) => Math.round(c + (255 - c) * ratio)
  const r = mix((n >> 16) & 255)
  const g = mix((n >> 8) & 255)
  const b = mix(n & 255)
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1).toUpperCase()}`
}

/** Цвет ника; без настройки — читаемый текст темы */
export const nickColor = (colors) => (isValidHex(colors?.nick) ? colors.nick : primaryTextColor(colors))

/**
 * Базовый текст внутри карточек профиля. Цвета карточек не зависят от фона страницы
 * `bg`; для пользовательского `content_bg` основной системный текст автоматически
 * выбирает `cardPrimaryColor`. primaryTextColor/secondaryTextColor — только для
 * текста, который лежит прямо на фоне профиля (живой предпросмотр настроек).
 */
export const CARD_TEXT = PROFILE_THEME.text
export const CARD_SECONDARY = PROFILE_THEME.textSecondary
export const cardNickColor = (colors) => (isValidHex(colors?.nick) ? colors.nick : PROFILE_THEME.text)

/**
 * Фон карточек контента (все, кроме карточек скинов): блоки статистики,
 * активности, рефералов, баланса, «последняя ставка», шапка профиля.
 * null — цвет стандартной темы (bg-surface), не зависит от фона страницы.
 */
export const contentBgColor = (colors) => (isValidHex(colors?.content_bg) ? colors.content_bg : PROFILE_THEME.surface)

/**
 * Фон stat-плиток (активность/рефералы): content_bg, null — цвет темы для
 * плиток (bg-dark-primary, темнее карточек) — старый вид профиля.
 */
export const tileBgColor = (colors) => (isValidHex(colors?.content_bg) ? colors.content_bg : PROFILE_THEME.bg)

export const skinCardBgColor = (colors) => (isValidHex(colors?.card_bg) ? colors.card_bg : PROFILE_THEME.surface)

/**
 * Пользовательский цвет текстовых подписей hero-блока профиля. Значение
 * применяется напрямую без коррекции контраста; null — цвет стандартной темы.
 */
export const cardSecondaryColor = (colors) => (isValidHex(colors?.text_secondary) ? colors.text_secondary : CARD_SECONDARY)

export const primaryColorForBackground = (background) => (
  contrastRatio(CARD_TEXT, background) >= contrastRatio(PROFILE_THEME.surface, background)
    ? CARD_TEXT
    : PROFILE_THEME.surface
)

export const cardPrimaryColor = (colors) => primaryColorForBackground(contentBgColor(colors))
export const tilePrimaryColor = (colors) => primaryColorForBackground(tileBgColor(colors))
export const skinCardPrimaryColor = (colors) => primaryColorForBackground(skinCardBgColor(colors))
